import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../supabaseClient';
import { captureConnectionsEvent } from '../lib/captureConnectionsEvent';
import { daysUntilExam } from '../lib/examDate';
import { ExamDateField } from './Connections/ExamCountdown';

/**
 * Shared create-workspace modal (Connections sidebar).
 */
const CreateWorkspaceModal = ({ isOpen, onClose, onCreated }) => {
  const [workspaceName, setWorkspaceName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    if (isCreating) return;
    setWorkspaceName('');
    setExamDate('');
    setError('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!workspaceName.trim() || isCreating) return;

    setIsCreating(true);
    setError('');

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error('No active session');

      const { data, error: insertError } = await supabase
        .from('workspaces')
        .insert([
          {
            name: workspaceName.trim(),
            user_id: user.id,
            is_public: false,
            exam_date: examDate || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ])
        .select();

      if (insertError) throw insertError;

      if (data?.[0]) {
        if (examDate) {
          captureConnectionsEvent('exam_date_set', {
            source: 'create',
            days_until: daysUntilExam(examDate),
          });
        } else {
          captureConnectionsEvent('exam_date_skipped', { source: 'create' });
        }

        setWorkspaceName('');
        setExamDate('');
        onCreated(data[0]);
      }
    } catch (createError) {
      console.error('Error creating workspace:', createError);
      setError(createError.message || 'Failed to create workspace. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-workspace-title"
    >
      <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-md w-full">
        <div className="px-6 py-4 border-b-2 border-neutral-200 flex justify-between items-center">
          <h3 id="create-workspace-title" className="text-xl font-semibold text-neutral-900">
            Create New Workspace
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
            aria-label="Close"
            disabled={isCreating}
          >
            <XMarkIcon className="h-5 w-5 text-neutral-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-5">
            <div>
              <label
                htmlFor="connections-workspace-name"
                className="block text-sm font-semibold text-neutral-900 mb-2"
              >
                Workspace name
              </label>
              <input
                id="connections-workspace-name"
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="e.g. USMLE, PLAB, Finals"
                className="w-full px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none transition-colors text-base"
                autoFocus
                disabled={isCreating}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-neutral-900 mb-2">
                When&apos;s your exam?{' '}
                <span className="font-normal text-neutral-500">Optional</span>
              </label>
              <ExamDateField value={examDate} onChange={setExamDate} />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setExamDate('')}
                  disabled={isCreating}
                  className="text-sm font-medium text-neutral-600 underline underline-offset-2 hover:text-neutral-900 disabled:opacity-40"
                >
                  Not sure yet
                </button>
              </div>
            </div>

            {error && (
              <p role="alert" className="text-sm text-cranberry-700">
                {error}
              </p>
            )}
          </div>

          <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2.5 text-neutral-900 hover:text-cranberry-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !workspaceName.trim()}
              className={`px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium
                ${
                  isCreating || !workspaceName.trim()
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-cranberry-600 transition-colors'
                }`}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkspaceModal;
